"use client";

import type { UseMutationResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import * as React from "react";
import { useStoreSlug } from "@/hooks/use-store-slug";
import { useStores } from "@/hooks/use-stores";
import type { CreateCategoryResponse } from "../adapters/create-category.adapter";
import type { DeleteCategoryResponse } from "../adapters/delete-category.adapter";
import { getCategoriesAdapter } from "../adapters/get-categories.adapter";
import type { Category } from "../types";

/**
 * Shared query definition reused by the route loader and the hook so both touch
 * the same cache entry (key + fn identical).
 */
export function categoriesQueryOptions(
	storeSlug: string | undefined,
	storeId: string | undefined,
) {
	return {
		queryKey: ["categories", storeSlug, storeId] as const,
		queryFn: () => getCategoriesAdapter(storeId),
	};
}

interface UseCategoriesMutations {
	createCategory?: UseMutationResult<
		CreateCategoryResponse,
		Error,
		{ name: string; parentId: string | null; hidden?: boolean }
	>;
	updateCategory?: UseMutationResult<
		CreateCategoryResponse,
		Error,
		{ id: string; name: string; parentId?: string | null; sortOrder?: number; hidden?: boolean }
	>;
	deleteCategory?: UseMutationResult<DeleteCategoryResponse, Error, string>;
}

export function useCategories(mutations?: UseCategoriesMutations) {
	const storeSlug = useStoreSlug();
	const { stores } = useStores();
	const [expandedIds, setExpandedIds] = React.useState<Set<string>>(new Set());

	// Obter storeId a partir do slug
	const currentStore = React.useMemo(() => {
		if (!storeSlug) return null;
		return stores.find((store) => store.slug === storeSlug) || null;
	}, [storeSlug, stores]);

	const {
		data: categories = [],
		isLoading,
		error,
	} = useQuery({
		...categoriesQueryOptions(storeSlug, currentStore?.id),
		enabled: !!currentStore,
	});

	const toggleExpanded = React.useCallback((id: string) => {
		setExpandedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	}, []);

	const findCategoryById = React.useCallback(
		(cats: Category[], id: string): Category | undefined => {
			for (const cat of cats) {
				if (cat.id === id) return cat;
				const found = findCategoryById(cat.children, id);
				if (found) return found;
			}
			return undefined;
		},
		[],
	);

	const addCategory = React.useCallback(
		(parentId: string | null, name: string) => {
			if (mutations?.createCategory) {
				// Usar mutation da API
				mutations.createCategory.mutate({
					name,
					parentId,
					hidden: false,
				});
				// Expandir parent se for subcategoria
				if (parentId) {
					setExpandedIds((prev) => new Set([...prev, parentId]));
				}
			}
		},
		[mutations],
	);

	const updateCategory = React.useCallback(
		(id: string, name: string) => {
			if (mutations?.updateCategory) {
				const category = findCategoryById(categories, id);
				if (category) {
					// Usar mutation da API
					mutations.updateCategory.mutate({
						id,
						name,
						parentId: category.parentId,
						hidden: !category.isActive,
					});
				}
			}
		},
		[mutations, categories, findCategoryById],
	);

	const toggleHidden = React.useCallback(
		(id: string) => {
			if (mutations?.updateCategory) {
				const category = findCategoryById(categories, id);
				if (category) {
					mutations.updateCategory.mutate({
						id,
						name: category.name,
						parentId: category.parentId,
						hidden: category.isActive,
					});
				}
			}
		},
		[mutations, categories, findCategoryById],
	);

	const deleteCategory = React.useCallback(
		(id: string) => {
			if (mutations?.deleteCategory) {
				// Usar mutation da API
				mutations.deleteCategory.mutate(id);
			}
		},
		[mutations],
	);

	const moveCategory = React.useCallback(
		(draggedId: string, targetId: string) => {
			if (!mutations?.updateCategory) return;

			const draggedCategory = findCategoryById(categories, draggedId);
			const targetCategory = findCategoryById(categories, targetId);
			if (!draggedCategory || !targetCategory) return;

			// Só reordena dentro do mesmo nível — nunca aninhar via drag-and-drop
			if (draggedCategory.parentId !== targetCategory.parentId) return;

			mutations.updateCategory.mutate({
				id: draggedId,
				name: draggedCategory.name,
				parentId: draggedCategory.parentId,
				sortOrder: targetCategory.sortOrder,
				hidden: !draggedCategory.isActive,
			});
		},
		[mutations, categories, findCategoryById],
	);

	return {
		categories,
		expandedIds,
		toggleExpanded,
		addCategory,
		updateCategory,
		toggleHidden,
		deleteCategory,
		moveCategory,
		findCategoryById,
		isLoading,
		error,
	};
}
